import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import InstagramIcon from "@/assets/dark_gradient/instagram.svg?react";
import LockIcon from "@/assets/dark_gradient/lock.svg?react";
import TiktokIcon from "@/assets/dark_gradient/tiktok.svg?react";
import UgcIcon from "@/assets/dark_gradient/ugc.svg?react";
import CircleWithAiSparkIcon from "@/assets/purple_gradient/circle_with_ai_spark.svg?react";
import CampaignTile from "@/components/Campaign/management/CampaignTile";
import ReusableDropdown, {TDropdownOption} from "@/components/Common/forms/ReusableDropdown";
import GenerativeAiLock from "@/components/Common/GenerativeAiLock";
import GradientButton from "@/components/Common/GradientButton";
import Modal from "@/components/Common/Modal";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useGenerativeAiAllowed} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {useMiscActions} from "@/store/campaignForm";
import {createCampaignForCompanyAtom, createCampaignFromGroupAtom} from "@/store/jotaiAtoms";
import type {TCampaignCreationType} from "@/store/types";
import {useNavigate} from "@tanstack/react-router";
import {useAtom} from "jotai";
import type {FC, ReactNode} from "react";
import {Fragment} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce} from "react-use";

type TCampaignTypeOption = TDropdownOption<{
  disabledReason?: string;
  icons: ReactNode[];
  isLocked?: boolean;
  lockedLabel?: string;
  value: TCampaignCreationType;
}>;

const CreateCampaignModal: FC = () => {
  const {t} = useTranslation(["common", "campaign_creation"]);
  const navigate = useNavigate();
  const actions = useMiscActions();
  const [createCampaignForCompany, setCreateCampaignForCompany] = useAtom(createCampaignForCompanyAtom);
  const [createCampaignFromGroup, setCreateCampaignFromGroup] = useAtom(createCampaignFromGroupAtom);
  const {hasSubscription, isInstagramEnabled, isTiktokEnabled} = usePackageLimits();
  const generativeAiAllowed = useGenerativeAiAllowed();

  const missingInstagramMembers = createCampaignFromGroup?.instagramCount === 0;
  const missingTiktokMembers = createCampaignFromGroup?.tiktokCount === 0;
  const missingUgcMembers = createCampaignFromGroup?.ugcCount === 0;

  useEffectOnce(() => {
    actions.resetStore();
  });

  const getOptions = (): TCampaignTypeOption[] => {
    const campaignOptions: TCampaignTypeOption[] = [
      {
        disabledReason: missingInstagramMembers
          ? t("campaign_creation:no_platform_members_in_group", {platform: t("instagram")})
          : undefined,
        icons: [<InstagramIcon className="size-3.5 shrink-0" />],
        isLocked: hasSubscription && !isInstagramEnabled,
        label: t("instagram_campaign"),
        lockedLabel: t("campaign_creation:add_campaign_type_to_your_account", {type: t("instagram")}),
        value: "instagram",
      },
      {
        disabledReason: missingTiktokMembers
          ? t("campaign_creation:no_platform_members_in_group", {platform: t("tiktok")})
          : undefined,
        icons: [<TiktokIcon className="size-3.5 shrink-0" />],
        isLocked: hasSubscription && !isTiktokEnabled,
        label: t("tiktok_campaign"),
        lockedLabel: t("campaign_creation:add_campaign_type_to_your_account", {type: t("tiktok")}),
        value: "tiktok",
      },
    ];

    if ((isInstagramEnabled && isTiktokEnabled) || !hasSubscription) {
      campaignOptions.push({
        disabledReason:
          missingInstagramMembers || missingTiktokMembers
            ? t("campaign_creation:no_combined_members_in_group")
            : undefined,
        icons: [<InstagramIcon className="size-3.5 shrink-0" />, <TiktokIcon className="size-3.5 shrink-0" />],
        label: t("instagram_and_tiktok_campaign"),
        value: "instagram_and_tiktok",
      });
    }

    campaignOptions.push({
      disabledReason: missingUgcMembers
        ? t("campaign_creation:no_platform_members_in_group", {platform: t("ugc")})
        : undefined,
      icons: [<UgcIcon className="h-3 w-5 shrink-0" />],
      label: t("ugc_campaign"),
      value: "ugc",
    });

    return campaignOptions;
  };
  const options = getOptions();

  const selectOption = (option: TCampaignTypeOption) => {
    if (option.disabledReason) {
      return;
    }

    if (option.isLocked) {
      navigate({to: "/plans"});
      setCreateCampaignForCompany(null);
      setCreateCampaignFromGroup(null);
      return;
    }

    actions.initNewCampaign({
      campaignType: option.value,
      groupInvite: createCampaignFromGroup ?? undefined,
    });

    setCreateCampaignForCompany(null);
    setCreateCampaignFromGroup(null);
  };

  return (
    <Modal
      backgroundClassName="w-fit overflow-visible md:w-fit"
      onClose={() => {
        setCreateCampaignForCompany(null);
        setCreateCampaignFromGroup(null);
      }}
      modalTitle={t("chooseCampaignStyle")}
      customBottomRow={<></>}
      className="flex flex-col gap-2.5 overflow-visible">
      <GenerativeAiLock locked={!generativeAiAllowed} place="right">
        <CampaignTile
          chipTitle={t("save_value_mins", {value: 15})}
          description={t("ai_campaign_creation_description")}
          Icon={CircleWithAiSparkIcon}
          onClick={() => {
            const companyId = createCampaignForCompany ?? undefined;
            setCreateCampaignForCompany(null);
            if (companyId) {
              navigate({to: "/company/$companyId/welcome", params: {companyId}});
            } else {
              navigate({to: "/welcome"});
            }
          }}
          title={t("ai_campaign_creation")}
        />
      </GenerativeAiLock>
      <ReusableDropdown<TCampaignTypeOption>
        dropdownClassName="border-neutral-white-dark divide-neutral-white-dark"
        options={options}
        renderButton={({isOpen, onClick}) => (
          <div
            className={clsxm(
              "hover:bg-primary-light-solid group border-neutral-white-dark flex min-h-13.5 cursor-pointer items-center justify-between gap-2 rounded-md border p-5 py-3 transition-all duration-150 select-none",
              isOpen && "bg-primary-light-solid",
            )}
            data-testid="create-campaign-manually-option"
            onClick={onClick}>
            <span
              className={clsxm(
                "group-hover:text-primary-dark-solid text-xl font-semibold transition-all duration-150",
                isOpen && "text-primary-dark-solid",
              )}>
              {t("campaign_creation:create_campaign_manually")}
            </span>
            <RightChevronIcon
              className={clsxm("text-neutral-black size-2.5 transition-all", isOpen ? "-rotate-90" : "rotate-90")}
            />
          </div>
        )}
        renderItem={({option, onClick}) => (
          <li
            className={clsxm(
              "hover:bg-neutral-grey-lightest flex min-h-11 cursor-pointer items-center justify-between gap-2 px-5 py-3 select-none",
              option.isLocked && "bg-neutral-grey-lightest",
              option.disabledReason && "bg-neutral-grey-lightest cursor-not-allowed",
            )}
            data-testid={`campaign-type-${option.value}`}
            onClick={option.disabledReason ? undefined : onClick}>
            <div className={clsxm("flex items-center gap-2", option.disabledReason && "opacity-50")}>
              <div className="flex gap-1">
                {option.icons.map((icon, index) => (
                  <Fragment key={index}>{icon}</Fragment>
                ))}
              </div>
              <span className="text-xs">{option.label}</span>
            </div>
            {option.disabledReason ? (
              <span className="text-neutral-grey-dark text-2xs max-w-40 text-right leading-3.5">
                {option.disabledReason}
              </span>
            ) : (
              option.isLocked && (
                <div className="flex flex-col items-end gap-2">
                  <span className="text-neutral-grey-dark text-2xs leading-3.5">{option.lockedLabel}</span>
                  <GradientButton
                    Icon={<LockIcon className="size-2.5 shrink-0" />}
                    title={t("campaign_creation:unlock")}
                    className="hover-none h-fit w-fit scale-[1.01] p-px"
                  />
                </div>
              )
            )}
          </li>
        )}
        selectedOption={undefined}
        setSelectedOption={selectOption}
      />
    </Modal>
  );
};

export default CreateCampaignModal;
